import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { TrendingUp, Shield, FileText, Zap, ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import { UpliftLogo, UpliftLogoWhite } from '../components/UpliftLogo';

const CreditBoostLanding = () => {
  const features = [
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Score Simulator',
      description: 'See exactly how different actions will impact your credit score before you take them.',
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'AI Action Plans',
      description: 'Get a personalized 30 or 90-day roadmap to boost your score fast.',
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'Dispute Letters',
      description: 'Generate professional dispute letters to remove negative items from your report.',
    },
  ];

  const stats = [
    { value: '50-150', label: 'Point increase potential' },
    { value: '30 days', label: 'To see first results' },
    { value: '$49', label: 'One-time investment' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center gap-2" data-testid="uplift-home-link">
                <UpliftLogo size="sm" showText={false} />
              </Link>
              <Link to="/creditboost" className="flex items-center gap-2" data-testid="creditboost-logo">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  CreditBoost
                </span>
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" data-testid="creditboost-login-btn">Log in</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-blue-600 hover:bg-blue-700" data-testid="creditboost-get-started-btn">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              AI-Powered Credit Repair
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 leading-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Boost Your Credit Score <span className="text-blue-600">Fast</span>
            </h1>
            <p className="text-xl text-slate-600 mb-8">
              Stop guessing. Get a personalized action plan to improve your credit score by 50-150 points in as little as 30 days.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200" data-testid="creditboost-hero-cta">
                  Start Improving Today - $49
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/creditboost/pricing">
                <Button size="lg" variant="outline" data-testid="creditboost-view-pricing">
                  View All Plans
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-3 gap-8 max-w-2xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl font-bold text-blue-600">{stat.value}</p>
                <p className="text-sm text-slate-600 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem/Solution */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Bad Credit Costs You Money Every Day
          </h2>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div className="p-6">
              <p className="text-4xl font-bold text-red-400">$200k+</p>
              <p className="text-slate-400 mt-2">Extra interest paid on a mortgage with bad credit</p>
            </div>
            <div className="p-6">
              <p className="text-4xl font-bold text-red-400">15%+</p>
              <p className="text-slate-400 mt-2">Higher car insurance premiums</p>
            </div>
            <div className="p-6">
              <p className="text-4xl font-bold text-red-400">Denied</p>
              <p className="text-slate-400 mt-2">Jobs, apartments, and loans</p>
            </div>
          </div>
          <p className="mt-12 text-xl text-blue-400">
            Fix your credit now. Your future self will thank you.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Your Complete Credit Repair Toolkit
            </h2>
            <p className="text-lg text-slate-600">
              Everything you need to take control of your credit
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="p-8 rounded-2xl bg-slate-50 hover:bg-white hover:shadow-xl transition-all border border-slate-200/50"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              How It Works
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Tell Us About Your Credit', desc: 'Answer a few questions about your current situation' },
              { step: '2', title: 'Get Your Action Plan', desc: 'AI generates a personalized improvement roadmap' },
              { step: '3', title: 'Follow the Steps', desc: 'Clear daily/weekly actions to boost your score' },
              { step: '4', title: 'Watch Your Score Rise', desc: 'See results in as little as 30 days' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                What You Get
              </h2>
              <div className="space-y-4">
                {[
                  'Personalized 30 or 90-day action plan',
                  'Credit score simulator',
                  'Dispute letter generator',
                  'Goodwill letter templates',
                  'Creditor negotiation scripts',
                  'Step-by-step instructions',
                  'Progress tracking',
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-600" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-8 text-white">
              <div className="text-center">
                <p className="text-blue-200 mb-2">Most Popular</p>
                <p className="text-5xl font-bold mb-2">$49</p>
                <p className="text-blue-200 mb-6">one-time payment</p>
                <Link to="/register">
                  <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 w-full">
                    Get Your Action Plan
                  </Button>
                </Link>
                <p className="text-sm text-blue-200 mt-4">30-day money-back guarantee</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Fix Your Credit?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join others who've boosted their scores with CreditBoost
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 shadow-xl" data-testid="creditboost-cta-bottom">
              Start Your Credit Journey
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <UpliftLogoWhite size="md" showText={true} className="mb-4" />
              <p className="text-slate-400 text-sm max-w-md">
                Uplift helps you rise in every area that matters. 
                Career tools, business planning, and credit repair - all in one place.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Products</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/careerboost" className="text-slate-400 hover:text-white">CareerBoost</Link></li>
                <li><Link to="/bizplanai" className="text-slate-400 hover:text-white">BizPlanAI</Link></li>
                <li><Link to="/creditboost" className="text-slate-400 hover:text-white">CreditBoost</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/terms" className="text-slate-400 hover:text-white">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-slate-400 hover:text-white">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <p className="text-slate-400 text-sm">
                  Founded by <span className="text-white font-medium">Robert Elder III</span>, CEO
                </p>
                <p className="text-slate-500 text-xs mt-1">
                  © 2024-2025 Uplift III / Robert Elder III. All Rights Reserved.
                </p>
              </div>
              <p className="text-slate-600 text-xs text-center md:text-right max-w-md">
                Uplift™, CareerBoost™, BizPlanAI™, and CreditBoost™ are trademarks of Robert Elder III. 
                Unauthorized copying is strictly prohibited.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CreditBoostLanding;
